import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { ExchangeIcon } from "src/Common/components/ExchangeIcon";
import { Exchange } from "src/Common/types";
import { isEqual } from "lodash";
import { getExchanges } from "src/Api";
import { EXCHANGES } from "src/Api/endpoints";
import { useArbitrageFilters } from "src/Modules/Arbitrage/context";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { EditFilterActions } from "../EditFilterActions";
import { FilterCatalogItem } from "src/Modules/Arbitrage/types";

interface Props {
  filterName: FilterCatalogItem["name"];
}

export const ExcludedExchangesAutocomplete = ({ filterName }: Props) => {
  const { data: exchanges, isLoading } = useQuery(EXCHANGES, getExchanges);

  const { activeFiltersByName } = useArbitrageFilters();

  const prevFilterValue = useMemo(
    () => (activeFiltersByName[filterName] ?? []) as string[],
    [filterName, activeFiltersByName]
  );

  const [excluded, setExcluded] = useState<Exchange[]>([]);

  useEffect(() => {
    prevFilterValue?.length &&
      exchanges?.length &&
      setExcluded(
        exchanges?.filter((e) => prevFilterValue.includes(e.id) ?? [])
      );
  }, [exchanges, prevFilterValue]);

  useEffect(() => {
    if (prevFilterValue?.length && exchanges?.length) {
      setExcluded(
        exchanges?.filter((e) => prevFilterValue.includes(e.id) ?? [])
      );
    } else {
      setExcluded([]);
    }
  }, [exchanges, prevFilterValue]);

  const handleToggleSelectAll = () => {
    excluded.length
      ? setExcluded([])
      : exchanges?.length && setExcluded(exchanges);
  };

  const allSelected = useMemo(
    () => isEqual(excluded, exchanges),
    [excluded, exchanges]
  );

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end">
      <FormControlLabel
        label="Select all"
        control={
          <Checkbox
            onChange={handleToggleSelectAll}
            checked={allSelected}
            indeterminate={!allSelected && Boolean(excluded.length)}
          />
        }
      />
      <Autocomplete
        multiple
        loading={isLoading}
        onChange={(_, newValue) => setExcluded(newValue)}
        value={excluded}
        style={{ width: 400, paddingTop: ".5rem" }}
        disableCloseOnSelect
        id="exchanges-to-exclude"
        options={exchanges ?? []}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              avatar={
                <ExchangeIcon
                  style={{ height: "1rem", width: "1rem" }}
                  name={option.name}
                />
              }
              size="small"
              label={option.name}
              {...getTagProps({ index })}
            />
          ))
        }
        renderOption={(props, option) => (
          <Box
            sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
            component="li"
            {...props}
          >
            <ExchangeIcon style={{ height: "1.5rem" }} name={option.id} />
            <span>{option.name}</span>
          </Box>
        )}
        getOptionLabel={(option) => option.name}
        renderInput={(params) => (
          <TextField
            variant="outlined"
            label="Exchanges to exclude"
            {...params}
          />
        )}
      />
      <EditFilterActions editValue={excluded.map((e) => e.id)} />
    </Box>
  );
};
